import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API, formatApiErrorDetail } from "@/lib/api";
import { CS } from "@/constants/testIds/clickshield";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      toast.success("Password updated — log in with your new password");
      nav("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#040914] bg-tech-grid flex items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute -top-32 right-0 w-[30rem] h-[30rem] rounded-full bg-cyan-neon/10 blur-[130px] pointer-events-none" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10">
        <div className="flex items-center gap-2.5 justify-center mb-8">
          <ShieldCheck className="w-8 h-8 text-cyan-neon" />
          <span className="font-display text-xl text-white">ClickShield<span className="text-cyan-neon"> AI</span></span>
        </div>
        <div className="rounded-2xl neon-border bg-[#0B101A]/80 backdrop-blur-xl p-8">
          <h1 className="font-display text-2xl text-white mb-1.5">Set a new password</h1>
          <p className="text-sm text-slate-400 mb-7">Choose a strong password (min. 6 characters).</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="font-mono text-[11px] tracking-widest uppercase text-slate-500">New password</label>
              <input
                data-testid={CS.resetPasswordInput}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                placeholder="••••••••"
                className="mt-2 w-full px-4 py-3 rounded-lg bg-[#040914] border border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-neon/50 focus:outline-none transition-colors"
              />
            </div>
            {error && (
              <p data-testid={CS.resetError} className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                {error}
              </p>
            )}
            <button
              data-testid={CS.resetSubmit}
              type="submit"
              disabled={loading || !token}
              className="w-full py-3 rounded-full bg-cyan-neon text-[#040914] font-semibold hover:glow-cyan transition-shadow inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
            </button>
          </form>
          {!token && (
            <p className="mt-4 text-sm text-amber-400">This link is missing its token — request a new one from the <Link to="/forgot-password" className="underline">forgot password</Link> page.</p>
          )}
        </div>
      </motion.div>
    </div>
  );
}
